import { NewsEvent } from "../api";

const importanceColor: Record<string, string> = {
  high: "text-danger font-bold",
  medium: "text-warn",
  low: "text-fg-muted",
  holiday: "text-fg-muted italic",
};

function timeUntil(isoTs: string, asOfIso: string): string {
  const t = new Date(isoTs).getTime();
  const now = new Date(asOfIso).getTime();
  const deltaMin = Math.round((t - now) / 60000);
  if (deltaMin === 0) return "now";
  const abs = Math.abs(deltaMin);
  if (abs < 60) return deltaMin > 0 ? `in ${deltaMin}m` : `${abs}m ago`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const s = `${h}h${m > 0 ? ` ${m}m` : ""}`;
  return deltaMin > 0 ? `in ${s}` : `${s} ago`;
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

interface Props {
  events: NewsEvent[];
  asOf: string | null;
}

export function NewsPanel({ events, asOf }: Props) {
  return (
    <section className="bg-surface border border-border rounded p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted">
          Economic calendar
        </h2>
        <span className="text-xs text-fg-muted font-mono">
          next {events.length} events
        </span>
      </div>

      {events.length === 0 ? (
        <p className="text-fg-muted text-sm">
          no events ingested · MQL5 calendar refreshes every few minutes via the
          bridge
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-mono">
            <thead className="text-xs uppercase text-fg-muted">
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-3">Time</th>
                <th className="text-left py-2 pr-3">In/Ago</th>
                <th className="text-left py-2 pr-3">Cur</th>
                <th className="text-left py-2 pr-3">Event</th>
                <th className="text-left py-2 pr-3">Impact</th>
                <th className="text-right py-2 pr-3">Prev</th>
                <th className="text-right py-2 pr-3">Forecast</th>
                <th className="text-right py-2 pr-3">Actual</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id} className="border-b border-border/60">
                  <td className="py-1 pr-3 text-fg-muted">
                    {fmtTime(ev.scheduled_time)}
                  </td>
                  <td className="py-1 pr-3 text-fg-muted">
                    {asOf ? timeUntil(ev.scheduled_time, asOf) : "—"}
                  </td>
                  <td className="py-1 pr-3">{ev.currency}</td>
                  <td className="py-1 pr-3">{ev.name}</td>
                  <td
                    className={`py-1 pr-3 uppercase ${
                      importanceColor[ev.importance] ?? ""
                    }`}
                  >
                    {ev.importance}
                  </td>
                  <td className="py-1 pr-3 text-right text-fg-muted">
                    {ev.previous_value ?? "—"}
                  </td>
                  <td className="py-1 pr-3 text-right text-fg-muted">
                    {ev.forecast_value ?? "—"}
                  </td>
                  <td
                    className={`py-1 pr-3 text-right ${ev.actual_value ? "" : "text-fg-muted"}`}
                  >
                    {ev.actual_value ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
